/**
 * Takvim ve oturum planlama.
 *
 * ── ONIZLEME TASARIMIN KALBI ─────────────────────────────────────────────────
 * Kullanici "Planla"ya basmadan ONCE gorur: kac oturum olusacak, kac krediye mal
 * olacak, bakiye ne kalacak, NE ENGELLIYOR. Engeller tek tek ve SEBEBIYLE
 * listelenir — "planlanamadı" demek kullaniciyi cikmaza sokar.
 *
 * Onizleme her degisiklikte otomatik yenilenir (gecikmeli). Kullanicinin ayrica
 * "hesapla" butonuna basmasi gerekmez; maliyet gorunmez bir sey olmamali.
 *
 * ── YA HEP YA HIC ────────────────────────────────────────────────────────────
 * Sunucu tek engel varsa hicbir sey olusturmuyor. Arayuz bunu acikca soyluyor:
 * engel varsa "Planla" butonu devre disi.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { copy, useI18n } from '@/lib/i18n';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  Modal,
  PageHeader,
  Select,
} from '@/components/ui';
import {
  formatDateTime,
  formatDuration,
  formatWeekday,
  sessionTone,
  SESSION_STATUS_LABEL,
  toLocalInputValue,
} from '@/lib/format';
import type {
  CatalogItem,
  CreateSessionRequest,
  Endpoint,
  SessionItem,
  SessionPlanPreview,
} from '@shared/index';
import './Schedule.css';

const WEEKDAYS_TR = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const WEEKDAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function defaultStart(): string {
  // Bir sonraki tam saat: kullanici genelde "birazdan" degil "saat basi" planlar.
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return toLocalInputValue(d);
}

function calendarDayKey(value: Date | string): string {
  return new Date(value).toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });
}

export default function Schedule() {
  const { language } = useI18n();
  const c = (turkish: string, english: string) => copy(language, turkish, english);
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';
  const toast = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [planning, setPlanning] = useState(() => params.get('planla') === '1');
  const [endpointFilter, setEndpointFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pendingCancel, setPendingCancel] = useState<{ kind: 'single' | 'series'; id: string; name: string } | null>(null);

  function closePlanning() {
    setPlanning(false);
    if (params.has('planla')) {
      const next = new URLSearchParams(params);
      next.delete('planla');
      setParams(next, { replace: true });
    }
  }

  const [range, setRange] = useState(() => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date(from);
    to.setDate(to.getDate() + 7);
    return { from, to };
  });

  const sessions = useQuery({
    queryKey: ['sessions', range.from.toISOString(), range.to.toISOString()],
    queryFn: () =>
      api.get<{ items: SessionItem[]; total: number }>('/sessions', {
        from: range.from.toISOString(),
        to: range.to.toISOString(),
        pageSize: 100,
      }),
  });

  const cancelOne = useMutation({
    mutationFn: (id: string) => api.delete<{ refunded: number }>(`/sessions/${id}`),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['sessions'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
      setPendingCancel(null);
      toast.success(
        data.refunded > 0
          ? c(`Oturum iptal edildi, ${data.refunded} kredi iade edildi.`, `Session cancelled and ${data.refunded} credits refunded.`)
          : c('Oturum iptal edildi.', 'Session cancelled.'),
      );
    },
    onError: (err) =>
      toast.error(err instanceof ApiRequestError ? err.message : c('İptal edilemedi.', 'Could not cancel the session.')),
  });

  const cancelSeries = useMutation({
    mutationFn: (seriesId: string) =>
      api.post<{ cancelled: number; refunded: number }>('/sessions/cancel-batch', { seriesId }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['sessions'] });
      setPendingCancel(null);
      toast.success(c(`${data.cancelled} oturum iptal edildi, ${data.refunded} kredi iade edildi.`, `${data.cancelled} sessions cancelled and ${data.refunded} credits refunded.`));
    },
    onError: (err) =>
      toast.error(err instanceof ApiRequestError ? err.message : c('İptal edilemedi.', 'Could not cancel the sessions.')),
  });

  const items = sessions.data?.items ?? [];

  const visibleItems = useMemo(() => items.filter((item) =>
    (endpointFilter === 'all' || item.endpointName === endpointFilter)
    && (productFilter === 'all' || item.productName === productFilter)
    && (statusFilter === 'all' || item.status === statusFilter)), [items, endpointFilter, productFilter, statusFilter]);

  /** Yedi gunluk kolonlar; bos gunler de korunur ki takvim ritmi kaybolmasin. */
  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => {
    const day = new Date(range.from);
    day.setDate(day.getDate() + index);
    return day;
  }), [range.from]);

  const byDay = useMemo(() => {
    const map = new Map<string, SessionItem[]>();
    for (const s of visibleItems) {
      const key = calendarDayKey(s.startsAt);
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    return map;
  }, [visibleItems]);

  const endpointOptions = [...new Set(items.map((item) => item.endpointName))].sort((a, b) => a.localeCompare(b, locale));
  const productOptions = [...new Set(items.map((item) => item.productName))].sort((a, b) => a.localeCompare(b, locale));

  return (
    <div className="schedule-page stack stack--lg">
      <PageHeader
        title={c('Takvim', 'Schedule')}
        description={c('Planlanmış ve geçmiş oturumlar.', 'Scheduled and past sessions.')}
        actions={<Button variant="primary" onClick={() => setPlanning(true)}>{c('Oturum planla', 'Schedule session')}</Button>}
      />

      {sessions.isLoading ? (
        <LoadingBlock rows={5} label={c('Takvim yükleniyor', 'Loading schedule')} />
      ) : sessions.isError ? (
        <ErrorState message={c('Takvim alınamadı.', 'Could not load the schedule.')} onRetry={() => void sessions.refetch()} />
      ) : (
        <div className="schedule-shell">
          <aside className="schedule-filters" aria-label={c('Takvim filtreleri', 'Schedule filters')}>
            <Field label={c('Cihaz', 'Device')}>{({ id }) => <Select id={id} value={endpointFilter} onChange={(event) => setEndpointFilter(event.target.value)}><option value="all">{c('Tümü', 'All')}</option>{endpointOptions.map((name) => <option key={name}>{name}</option>)}</Select>}</Field>
            <Field label={c('İçerik', 'Content')}>{({ id }) => <Select id={id} value={productFilter} onChange={(event) => setProductFilter(event.target.value)}><option value="all">{c('Tümü', 'All')}</option>{productOptions.map((name) => <option key={name}>{name}</option>)}</Select>}</Field>
            <Field label={c('Durum', 'Status')}>{({ id }) => <Select id={id} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">{c('Tümü', 'All')}</option>{Object.entries(SESSION_STATUS_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select>}</Field>
          </aside>
          <section className="schedule-calendar" aria-label={c('Haftalık oturum takvimi', 'Weekly session schedule')}>
            <header className="schedule-calendar__head">
              <div className="schedule-calendar__nav">
                <Button size="sm" aria-label={c('Önceki hafta', 'Previous week')} onClick={() => setRange(({ from, to }) => { const nf = new Date(from); const nt = new Date(to); nf.setDate(nf.getDate() - 7); nt.setDate(nt.getDate() - 7); return { from: nf, to: nt }; })}>←</Button>
                <Button size="sm" aria-label={c('Sonraki hafta', 'Next week')} onClick={() => setRange(({ from, to }) => { const nf = new Date(from); const nt = new Date(to); nf.setDate(nf.getDate() + 7); nt.setDate(nt.getDate() + 7); return { from: nf, to: nt }; })}>→</Button>
                <strong>{range.from.toLocaleDateString(locale, { day: 'numeric', month: 'long' })} – {new Date(range.to.getTime() - 86_400_000).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
              </div>
              <Button size="sm" onClick={() => { const from = new Date(); from.setHours(0, 0, 0, 0); const to = new Date(from); to.setDate(to.getDate() + 7); setRange({ from, to }); }}>{c('Bu hafta', 'This week')}</Button>
            </header>
            <div className="schedule-week">
              {days.map((day) => {
                const key = calendarDayKey(day);
                const daySessions = byDay.get(key) ?? [];
                const today = new Date().toDateString() === day.toDateString();
                return <section className={`schedule-day ${today ? 'is-today' : ''}`} key={key}>
                  <header><strong className="num">{day.getDate()}</strong><span>{formatWeekday(key)}</span></header>
                  <div className="schedule-day__body">
                    {daySessions.length === 0 ? <span className="schedule-day__empty">{c('Oturum yok', 'No sessions')}</span> : daySessions.map((session) => <article className={`schedule-event schedule-event--${sessionTone(session.status)}`} key={session.id}>
                      <time className="num">{new Date(session.startsAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</time>
                      <strong>{session.productName}</strong>
                      <span>{session.endpointName} · {formatDuration(session.durationMinutes)}</span>
                      <Badge tone={sessionTone(session.status)}>{SESSION_STATUS_LABEL[session.status]}</Badge>
                      {session.status === 'scheduled' ? <div className="schedule-event__actions"><Button size="sm" variant="ghost" onClick={() => setPendingCancel({ kind: 'single', id: session.id, name: session.productName })}>{c('İptal', 'Cancel')}</Button>{session.seriesId ? <Button size="sm" variant="ghost" onClick={() => setPendingCancel({ kind: 'series', id: session.seriesId!, name: session.productName })}>{c('Seri', 'Series')}</Button> : null}</div> : null}
                    </article>)}
                  </div>
                </section>;
              })}
            </div>
            {visibleItems.length === 0 ? <div className="schedule-calendar__empty"><strong>{c('Bu haftada eşleşen oturum yok', 'No matching sessions this week')}</strong><span>{c('Yeni bir oturum planlayabilir veya filtreleri değiştirebilirsiniz.', 'Schedule a new session or change the filters.')}</span><Button variant="primary" onClick={() => setPlanning(true)}>{c('Oturum planla', 'Schedule session')}</Button></div> : null}
          </section>
        </div>
      )}

      <PlanDialog open={planning} onClose={closePlanning} />
      <ConfirmDialog
        open={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        title={pendingCancel?.kind === 'series' ? c('Oturum serisini iptal et', 'Cancel session series') : c('Oturumu iptal et', 'Cancel session')}
        description={pendingCancel?.kind === 'series'
          ? c(`“${pendingCancel.name}” için bu serideki tüm planlanmış oturumlar iptal edilecek ve uygun krediler iade edilecek.`, `All scheduled sessions in the “${pendingCancel.name}” series will be cancelled and eligible credits refunded.`)
          : c(`“${pendingCancel?.name ?? ''}” oturumu iptal edilecek ve uygun kredi kataloğuna iade edilecek.`, `The “${pendingCancel?.name ?? ''}” session will be cancelled and eligible credits returned to its catalog.`)}
        confirmLabel={c('İptal et', 'Cancel session')}
        danger
        loading={cancelOne.isPending || cancelSeries.isPending}
        onConfirm={() => {
          if (!pendingCancel) return;
          if (pendingCancel.kind === 'series') cancelSeries.mutate(pendingCancel.id);
          else cancelOne.mutate(pendingCancel.id);
        }}
      />
    </div>
  );
}

// ─── Planlama diyalogu ───────────────────────────────────────────────────────

function PlanDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { language } = useI18n();
  const c = (turkish: string, english: string) => copy(language, turkish, english);
  const toast = useToast();
  const qc = useQueryClient();

  const [productId, setProductId] = useState('');
  const [endpointIds, setEndpointIds] = useState<string[]>([]);
  const [startsAt, setStartsAt] = useState(defaultStart);
  const [duration, setDuration] = useState(60);
  const [repeatKind, setRepeatKind] = useState<'none' | 'daily' | 'weekly'>('none');
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [until, setUntil] = useState('');

  const endpoints = useQuery({
    queryKey: ['endpoints'],
    queryFn: () => api.get<{ items: Endpoint[] }>('/endpoints'),
    enabled: open,
  });

  const products = useQuery({
    queryKey: ['catalog', { ownedOnly: true }],
    queryFn: () => api.get<{ items: CatalogItem[] }>('/catalog', { ownedOnly: true }),
    enabled: open,
  });

  const body: CreateSessionRequest | null =
    productId && endpointIds.length > 0 && startsAt
      ? {
          productId,
          endpointIds,
          startsAt: new Date(startsAt).toISOString(),
          durationMinutes: duration,
          ...(repeatKind !== 'none'
            ? {
                repeat: {
                  kind: repeatKind,
                  ...(repeatKind === 'weekly' && weekdays.length ? { weekdays } : {}),
                  ...(until ? { until } : {}),
                },
              }
            : {}),
        }
      : null;

  const [preview, setPreview] = useState<(SessionPlanPreview & { truncated?: boolean }) | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  // Onizleme, degisiklikten 400 ms sonra otomatik. Her tusa basista istek
  // atmak sunucuyu bosuna yorar; hic atmamak maliyeti gorunmez yapar.
  useEffect(() => {
    if (!open || !body) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const payload = JSON.stringify(body);
    const timer = setTimeout(() => {
      setPreviewing(true);
      api
        .post<SessionPlanPreview & { truncated?: boolean }>('/sessions/preview', JSON.parse(payload))
        .then((data) => {
          setPreview(data);
          setPreviewError(null);
        })
        .catch((err: unknown) => {
          setPreview(null);
          setPreviewError(
            err instanceof ApiRequestError ? err.message : c('Önizleme alınamadı.', 'Could not load the preview.'),
          );
        })
        .finally(() => setPreviewing(false));
    }, 400);

    return () => clearTimeout(timer);
    // `body` her render'da yeni nesne; JSON'unu bagimlilik olarak kullaniyoruz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(body)]);

  const create = useMutation({
    mutationFn: () => api.post<{ created: SessionItem[]; creditsSpent: number }>('/sessions', body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['sessions'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
      toast.success(
        c(`${data.created.length} oturum planlandı. ${data.creditsSpent} kredi düşüldü.`, `${data.created.length} sessions scheduled. ${data.creditsSpent} credits used.`),
      );
      onClose();
      setProductId('');
      setEndpointIds([]);
      setPreview(null);
    },
    onError: (err) =>
      toast.error(err instanceof ApiRequestError ? err.message : c('Oturum oluşturulamadı.', 'Could not create the session.')),
  });

  const blocked = (preview?.blockers.length ?? 0) > 0;
  const canSubmit = Boolean(body) && preview !== null && !blocked && preview.affordable;

  // Cevrimdisi noktalar da LISTELENIR: bugun kapali olan bir ekrana yarin icin
  // plan yapmak mesru. Gercek engelleri onizleme gosterir.
  const availableEndpoints = endpoints.data?.items ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={c('Oturum planla', 'Schedule session')}
      description={c('İçeriği, çalışacağı cihazları ve zaman aralığını seçin; Hub uygunluk ve kredi kontrolünü kayıttan önce yapar.', 'Choose the content, devices, and time range. Hub checks compatibility and credits before saving.')}
      size="lg"
      footer={
        <>
          <Button onClick={onClose}>{c('Vazgeç', 'Cancel')}</Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            {preview ? c(`Planla (${preview.creditsRequired} kredi)`, `Schedule (${preview.creditsRequired} credits)`) : c('Planla', 'Schedule')}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label={c('Ürün', 'Product')} required>
          {({ id }) => (
            <Select id={id} value={productId} onChange={(e) => setProductId(e.target.value)}>
              <option value="">{c('Seç…', 'Select…')}</option>
              {(products.data?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <fieldset className="sc-fieldset">
          <legend className="field__label">{c('Cihazlar', 'Devices')}</legend>
          {endpoints.isLoading ? (
            <LoadingBlock rows={2} />
          ) : availableEndpoints.length === 0 ? (
            <p className="small muted">
              {c('Tanımlı cihaz yok. Önce ', 'No devices are defined. Add one from the ')}<Link to="/operasyon/ekranlar">{c('Cihazlar', 'Devices')}</Link>{c(' sayfasından ekleyin.', ' page first.')}
            </p>
          ) : (
            <div className="sc-endpoints">
              {availableEndpoints.map((e) => (
                <Checkbox
                  key={e.id}
                  label={`${e.name}${e.capabilities?.aspectRatio ? ` (${e.capabilities.aspectRatio})` : ''}`}
                  checked={endpointIds.includes(e.id)}
                  onChange={(ev) =>
                    setEndpointIds((prev) =>
                      ev.target.checked ? [...prev, e.id] : prev.filter((x) => x !== e.id),
                    )
                  }
                />
              ))}
            </div>
          )}
          <p className="small muted">
            {c('Birden fazla cihaz seçerseniz her biri için ayrı oturum oluşur ve kendi katalog kredisinden ayrı düşülür.', 'If you select multiple devices, Hub creates a separate session for each and deducts credits from the relevant catalog separately.')}
          </p>
        </fieldset>

        <div className="row" style={{ gap: 'var(--space-3)', alignItems: 'flex-start' }}>
          <Field label={c('Başlangıç', 'Start')} required>
            {({ id }) => (
              <Input
                id={id}
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
            )}
          </Field>

          <Field label={c('Süre (dakika)', 'Duration (minutes)')} required hint={c('En az 15, en çok 720.', 'Minimum 15, maximum 720.')}>
            {({ id }) => (
              <Input
                id={id}
                type="number"
                min={15}
                max={720}
                step={15}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              />
            )}
          </Field>
        </div>

        <Field label={c('Tekrar', 'Repeat')}>
          {({ id }) => (
            <Select
              id={id}
              value={repeatKind}
              onChange={(e) => setRepeatKind(e.target.value as typeof repeatKind)}
            >
              <option value="none">{c('Tekrarlama', 'Do not repeat')}</option>
              <option value="daily">{c('Her gün', 'Daily')}</option>
              <option value="weekly">{c('Haftalık', 'Weekly')}</option>
            </Select>
          )}
        </Field>

        {repeatKind === 'weekly' && (
          <fieldset className="sc-fieldset">
            <legend className="field__label">{c('Günler', 'Days')}</legend>
            <div className="sc-days">
              {(language === 'tr' ? WEEKDAYS_TR : WEEKDAYS_EN).map((label, index) => (
                <button
                  key={index}
                  type="button"
                  className={`sc-day ${weekdays.includes(index) ? 'is-on' : ''}`}
                  aria-pressed={weekdays.includes(index)}
                  onClick={() =>
                    setWeekdays((prev) =>
                      prev.includes(index) ? prev.filter((d) => d !== index) : [...prev, index],
                    )
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {repeatKind !== 'none' && (
          <Field label={c('Bitiş tarihi', 'End date')} required hint={c('Bu tarihe kadar (dahil) tekrar eder.', 'Repeats through this date, inclusive.')}>
            {({ id }) => (
              <Input id={id} type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
            )}
          </Field>
        )}

        {/* ── Onizleme ──────────────────────────────────────────────────────── */}
        <div className="sc-preview" aria-live="polite">
          {previewing ? (
            <LoadingBlock rows={2} label={c('Kredi ihtiyacı hesaplanıyor', 'Calculating credit requirement')} />
          ) : previewError ? (
            <p className="sc-blocker">{previewError}</p>
          ) : !preview ? (
            <p className="small muted">{c('Ürün, cihaz ve saat seçince kredi ihtiyacı burada görünecek.', 'Select a product, device, and time to see the credit requirement here.')}</p>
          ) : (
            <>
              <div className="sc-summary">
                <div>
                  <span className="small muted">{c('Oturum', 'Sessions')}</span>
                  <strong className="num">{preview.sessionCount}</strong>
                </div>
                <div>
                  <span className="small muted">{c('Kredi', 'Credits')}</span>
                  <strong className="num">{preview.creditsRequired}</strong>
                </div>
                <div>
                  <span className="small muted">{c('Kalan bakiye', 'Balance after')}</span>
                  <strong
                    className="num"
                    style={{ color: preview.affordable ? 'var(--text)' : 'var(--danger)' }}
                  >
                    {preview.balanceAfter}
                  </strong>
                </div>
              </div>

              {preview.truncated && (
                <p className="sc-warn">
                  {c(`Üst sınır nedeniyle yalnızca ilk ${preview.sessionCount} oturum oluşturulacak. Bitiş tarihini yaklaştırmayı düşünün.`, `Only the first ${preview.sessionCount} sessions will be created because of the limit. Consider choosing an earlier end date.`)}
                </p>
              )}

              {!preview.affordable && (
                <p className="sc-blocker">
                  {c(`Kredi yetersiz. ${preview.creditsRequired} gerekiyor, bakiyeniz daha az.`, `Insufficient credits. ${preview.creditsRequired} are required and your balance is lower.`)}
                </p>
              )}

              {preview.blockers.length > 0 && (
                <div className="stack stack--sm">
                  <strong className="small" style={{ color: 'var(--danger)' }}>
                    {c(`${preview.blockers.length} engel — hiçbiri çözülmeden oturum oluşturulmaz:`, `${preview.blockers.length} blockers — no session will be created until all are resolved:`)}
                  </strong>
                  <ul className="sc-blockers">
                    {preview.blockers.slice(0, 8).map((b, i) => (
                      <li key={i}>{b.message}</li>
                    ))}
                    {preview.blockers.length > 8 && (
                      <li className="muted">{c(`…ve ${preview.blockers.length - 8} tane daha`, `…and ${preview.blockers.length - 8} more`)}</li>
                    )}
                  </ul>
                </div>
              )}

              {preview.blockers.length === 0 && preview.slots.length > 0 && (
                <details className="sc-slots">
                  <summary className="small">{c('Oluşacak oturumları göster', 'Show sessions to be created')}</summary>
                  <ul>
                    {preview.slots.slice(0, 20).map((s, i) => (
                      <li key={i} className="small">
                        {s.endpointName} · {formatDateTime(s.startsAt)} ·{' '}
                        {formatDuration(s.durationMinutes)}
                      </li>
                    ))}
                    {preview.slots.length > 20 && (
                      <li className="small muted">{c(`…ve ${preview.slots.length - 20} tane daha`, `…and ${preview.slots.length - 20} more`)}</li>
                    )}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
